// Thin API client. Wraps fetch with the /api/v1 prefix, JSON handling, credentials
// (so the HttpOnly session cookie is sent), and a normalized error shape.

const BASE = '/api/v1';

// Which shell is active, derived from the URL. The server uses this to pick the
// matching per-surface session cookie, so the admin and portal sessions can coexist
// in one browser. Kept in sync with the router's /portal prefix.
function currentSurface(): string {
  return typeof location !== 'undefined' && location.pathname.startsWith('/portal') ? 'portal' : 'admin';
}

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      // Always send a JSON content-type: it keeps unsafe requests off the CSRF
      // middleware's form-submission path (which would otherwise reject a
      // bodyless POST that lacks Sec-Fetch-Site/Origin headers).
      headers: { 'Content-Type': 'application/json', 'X-Cadence-Surface': currentSurface() },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('Network error — please check your connection', 'NETWORK_ERROR', 0);
  }
  return parse<T>(res);
}

/** Upload a raw file (image, etc). Sends the file's own content-type as the body
 *  content-type, which keeps it off the CSRF form path. */
async function upload<T>(path: string, file: Blob): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Cadence-Surface': currentSurface() },
      body: file,
    });
  } catch {
    throw new ApiError('Network error — please check your connection', 'NETWORK_ERROR', 0);
  }
  return parse<T>(res);
}

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

/** Upload a raw file via XHR so we get real upload-progress events and stream the body
 *  straight from disk (fetch buffers large bodies and exposes no progress). `onProgress`
 *  receives a 0..1 fraction. The returned handle can `abort()` an in-flight upload. */
function uploadWithProgress<T>(path: string, file: Blob, onProgress?: (fraction: number) => void): UploadHandle<T> {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Cadence-Surface', currentSurface());
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => resolve(parseBody<T>(xhr.status, xhr.responseText));
    xhr.onerror = () => reject(new ApiError('Network error — please check your connection', 'NETWORK_ERROR', 0));
    xhr.onabort = () => reject(new ApiError('Upload cancelled', 'ABORTED', 0));
    try { xhr.send(file); } catch { reject(new ApiError('Upload failed to start', 'ERROR', 0)); }
  });
  return { promise, abort: () => xhr.abort() };
}

/** Download a binary response (e.g. a GDPR export ZIP) and save it via the browser.
 *  Uses an authenticated fetch so the session cookie + surface header travel with it.
 *  Filename comes from Content-Disposition when present, else `fallbackName`. */
async function download(path: string, fallbackName: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      headers: { 'X-Cadence-Surface': currentSurface() },
    });
  } catch {
    throw new ApiError('Network error — please check your connection', 'NETWORK_ERROR', 0);
  }
  if (!res.ok) {
    const text = await res.text();
    const json = text ? safeParse(text) : null;
    throw new ApiError(json?.error?.message ?? `Request failed (${res.status})`, json?.error?.code ?? 'ERROR', res.status);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  const name = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function parse<T>(res: Response): Promise<T> {
  return parseBody<T>(res.status, await res.text());
}

/** Shared response shaping for both fetch and XHR paths. */
function parseBody<T>(status: number, text: string): T {
  const json = text ? safeParse(text) : null;
  if (status < 200 || status >= 300) {
    const err = json?.error;
    throw new ApiError(err?.message ?? `Request failed (${status})`, err?.code ?? 'ERROR', status);
  }
  // Endpoints return either { data } (most) or a bare object (health).
  return (json && 'data' in json ? json.data : json) as T;
}

function safeParse(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  download: (path: string, fallbackName: string) => download(path, fallbackName),
  upload: <T>(path: string, file: Blob) => upload<T>(path, file),
  uploadWithProgress: <T>(path: string, file: Blob, onProgress?: (fraction: number) => void) =>
    uploadWithProgress<T>(path, file, onProgress),
};
